import { inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControlConfigType } from '@app/core/enums/form-control-config-type';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import {
  IFloatingPopupGroupInstance,
  IFloatingPopupInstance,
} from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';

@Injectable({ providedIn: 'root' })
export class FloatingPopupTabsService {
  private injector = inject(Injector);
  private popupActiveTab = new Map<string, WritableSignal<string | null>>();

  public getActive(id: string): WritableSignal<string | null> {
    if (!this.popupActiveTab.has(id)) {
      console.error(`Popup active tab with id ${id} does not exist`);
      return signal(null);
    }

    return this.popupActiveTab.get(id)!;
  }

  public setActive(id: string, data: string | null) {
    if (this.hasActive(id)) {
      const oldData = this.getActive(id)();

      if (oldData !== data) {
        this.getActive(id)!.set(data);
      }
    } else {
      this.popupActiveTab.set(id, signal(data));
    }
  }

  public hasActive(id: string) {
    return this.popupActiveTab.has(id);
  }

  public deleteFromActive(id: string) {
    this.popupActiveTab.delete(id);
  }

  public normalizeMinimizedPopupActiveTabs(minimizedPopups: IFloatingPopupInstance[]) {
    const groupIdsActiveTabsMap: Record<string, string | null> = {};

    minimizedPopups.forEach(popup => {
      const tab =
        this.getMinimizedPopupActiveTabForMixedTabsInGroup(popup.groupId, minimizedPopups) ||
        this.getActive(popup.id)();

      if (!groupIdsActiveTabsMap[popup.groupId]) {
        groupIdsActiveTabsMap[popup.groupId] = tab || null;
      }

      this.setActive(popup.id, groupIdsActiveTabsMap[popup.groupId]);
    });
  }

  public getFormControlConfig(
    popup: IFloatingPopupInstance,
    tabs: IFloatingPopupGroupInstance['tabs'] | undefined
  ): IEnumFormControlConfig<string> | null {
    if (!tabs) {
      return null;
    }

    return {
      ...tabs,
      id: `floating-popup-tabs-${popup.id}`,
      type: FormControlConfigType.Enum,
      defaultValue: tabs.values[0],
      getValue: () => toObservable(this.getActive(popup.id), { injector: this.injector }),
      valueChange: (value: string) => this.setActive(popup.id, value),
    };
  }

  private getMinimizedPopupActiveTabForMixedTabsInGroup(
    groupId: string,
    minimizedPopups: IFloatingPopupInstance[]
  ): string | null {
    const minimized = minimizedPopups.filter(p => p.groupId === groupId && !p.isPopupAnimationInProgress);

    if (minimized.length === 0) {
      return null;
    }

    const count: Record<string, number> = {};

    minimized.forEach(p => {
      const tab = this.getActive(p.id)();
      if (tab) {
        count[tab] = (count[tab] || 0) + 1;
      }
    });

    const mostFrequentTab = Object.keys(count).reduce<null | string>(
      (lastTab, tab) => (lastTab && count[lastTab] > count[tab] ? lastTab : tab),
      null
    );

    return mostFrequentTab;
  }

  public getMinimizedPopupActiveTabForGroup(groupId: string, minimizedPopups: IFloatingPopupInstance[]): string | null {
    const minimized = minimizedPopups.find(p => p.groupId === groupId && !p.isPopupAnimationInProgress);
    return minimized ? this.getActive(minimized.id)() : null;
  }

  public setGroupTab(groupId: string, tab: string | null, popups: IFloatingPopupInstance[]) {
    const minimizedPopups = popups.filter(p => p.groupId === groupId && p.isMinimized);
    minimizedPopups.forEach(popup => this.setActive(popup.id, tab));
  }
}
